import { Badge, Box, BoxProps, Flex, Heading, Image, Text } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { useDocumentTitle } from '../../helpers'

export interface IDetailHeroProps extends Omit<BoxProps, 'title'> {
  /** Selo do canto: o tipo do registro (`Evento`, `Concurso`). */
  kicker: ReactNode
  /** Cor do selo do canto — o que distingue os dois tipos de ficha no painel. */
  kickerColorScheme?: string
  /** Capa opcional. Sem ela o banner encolhe para só o texto, sem buraco no lugar da imagem. */
  image?: string | null
  /** Nome do registro. É o `h1` da página — uma ficha com `DetailHero` não leva `PageTitle`. */
  title: ReactNode
  /** Linha de apoio sob o título: datas, local, prazos. */
  subtitle?: ReactNode
  /** Selos de estado do registro (status, formato, visibilidade, edição). */
  badges?: ReactNode
  /** Linha de ações do banner — `HeroButton` e `HeroLinkButton`. */
  actions?: ReactNode
  /** Título da aba, para quando o `title` for um `ReactNode`. Uma string já vale por si. */
  documentTitle?: string
}

/**
 * Banner escuro que abre a ficha de um registro: selo do tipo, capa, selos de estado, o nome
 * do registro como `h1`, uma linha de apoio e a fileira de ações.
 *
 * `ContestDetailPage` e `EventDetailPage` desenhavam este bloco com ~60 linhas praticamente
 * iguais — o mesmo `gray.900` com raio `xl`, a mesma capa de 180/260px em `opacity={0.6}`, o
 * mesmo degradê `linear(to-t, gray.900, transparent)` cobrindo 60% dela e o mesmo
 * `mt="-60px"` que sobe o texto por cima. As divergências eram acidentais: a fileira de selos
 * era `mb={3}` com quebra de linha numa página e `mb={2}` sem quebra na outra.
 *
 * **O `h1` da página sai daqui.** As duas páginas montavam um `<Heading as="h1">` à mão — eram
 * duas das treze do levantamento —, e é por isso que o título não é `PageTitle` nem é uma prop
 * de decoração: é o mesmo papel, num banner em vez de numa linha.
 */
export default function DetailHero({
  kicker,
  kickerColorScheme = 'blue',
  image,
  title,
  subtitle,
  badges,
  actions,
  mb = 6,
  documentTitle,
  ...rest
}: IDetailHeroProps) {
  // Mesmo motivo do `PageTitle`: quem emite o `h1` emite o título da aba.
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : undefined))

  return (
    <Box borderRadius="xl" overflow="hidden" bg="gray.900" position="relative" mb={mb} {...rest}>
      <Badge
        position="absolute"
        top={3}
        left={3}
        zIndex={2}
        colorScheme={kickerColorScheme}
        py={1}
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {kicker}
      </Badge>

      {image && (
        <Box position="relative" h={{ base: '180px', md: '260px' }}>
          <Image src={image} alt="" w="100%" h="100%" objectFit="cover" opacity={0.6} />
          {/* O degradê é o que garante o contraste do texto sobre a capa, seja ela clara ou escura. */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            h="60%"
            bgGradient="linear(to-t, gray.900, transparent)"
          />
        </Box>
      )}

      {/* Com capa o texto sobe 60px por cima dela; sem capa o banner é só este bloco. */}
      <Box px={6} pb={5} pt={image ? 0 : 5} mt={image ? '-60px' : 0} position="relative" zIndex={1}>
        {badges && (
          <Flex gap={2} mb={3} flexWrap="wrap">
            {badges}
          </Flex>
        )}

        <Heading as="h1" color="white" fontWeight="700" lineHeight="1.2">
          {title}
        </Heading>

        {subtitle && (
          <Text as="div" color="whiteAlpha.700" fontSize="sm" mt={2}>
            {subtitle}
          </Text>
        )}

        {actions && (
          <Flex gap={3} mt={4} flexWrap="wrap">
            {actions}
          </Flex>
        )}
      </Box>
    </Box>
  )
}
